import { normalizeContactPhoneToE164 } from '@/lib/contacts/phone';

/**
 * The customer's way of reaching the business from the tracking page.
 *
 * `tel:` and `sms:` targets are handed to the OS, and the OS is fussy about
 * them: a stored number like "+1 (604) 555-0100 ext. 12" carries parentheses,
 * dots and a spelled-out extension that some dialers refuse outright, which is
 * how a Call link ends up doing nothing when tapped. Everything on this page
 * that opens a phone app goes through here so the number is dialable first.
 */

/** Minimum digits before a string is worth handing to a dialer at all. */
const MIN_DIALABLE_DIGITS = 7;

/**
 * A number in the form a phone app will accept, or `null` when the business has
 * nothing dialable on file — the caller hides its Call/Message buttons rather
 * than offering a link that opens an empty dialer.
 *
 * E.164 is the target, but a number libphonenumber declines to parse is not
 * automatically undialable (a short international line, an unusual country), so
 * a plain digits-and-plus reading stands in when it has enough digits to be a
 * real number.
 */
export function dialablePhone(phone: string | null | undefined): string | null {
  const trimmed = phone?.trim();
  if (!trimmed) return null;

  const e164 = normalizeContactPhoneToE164(trimmed);
  if (e164) return e164;

  // Keep a single leading "+" and drop everything the dialer can't read.
  const stripped = trimmed.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
  const digits = stripped.replace(/\D/g, '');
  return digits.length >= MIN_DIALABLE_DIGITS ? stripped : null;
}

/** `tel:` / `sms:` hrefs for the business line, or null when it can't be dialed. */
export function businessContactLinks(
  phone: string | null | undefined,
): { tel: string; sms: string } | null {
  const dialable = dialablePhone(phone);
  if (!dialable) return null;
  // No `body` on the sms: link — iOS and Android disagree on the separator, and
  // an unwanted draft is worse than an empty one the customer types into.
  return { tel: `tel:${dialable}`, sms: `sms:${dialable}` };
}
